import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, Droplet, CreditCard, FileText,
  TrendingUp, Package, Settings, DollarSign, UserCheck,
  Calendar, Receipt, AlertCircle, Truck, Building2, Fuel,
  Store, Clock, Wallet, MapPin, ChevronDown, ShieldCheck,
  Loader2, BarChart2, BookOpen, Tag, ClipboardList,
  ShoppingBag, Banknote, BarChart, UsersRound, Star,
  Layers, Activity,
} from "lucide-react";
import { Sidebar, SidebarContent } from "@/components/ui/sidebar";
import { useUserRole } from "@/hooks/useUserRole";
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureKey } from "@/lib/features";

/* ── Animated collapsible ──────────────────────────────────────── */
function Section({
  label, icon: Icon, open, onToggle, children,
}: {
  label: string; icon: any; open: boolean;
  onToggle: () => void; children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(0);

  useEffect(() => {
    if (ref.current) setH(open ? ref.current.scrollHeight : 0);
  }, [open, children]);

  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[13px] font-semibold transition-colors duration-150 group
          ${open ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"}`}
      >
        <span className="flex items-center gap-2">
          <Icon className={`h-[15px] w-[15px] flex-shrink-0 ${open ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"}`} />
          {label}
        </span>
        <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`} />
      </button>

      <div style={{ height: h, overflow: "hidden", transition: "height 200ms ease" }}>
        <div ref={ref} className="mt-0.5 ml-3 pl-2.5 border-l border-gray-100 space-y-0.5 pb-1">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ── Nav link item ──────────────────────────────────────────────── */
function Item({ to, icon: Icon, label }: { to: string; icon?: any; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] font-medium transition-colors duration-100
        ${isActive
          ? "bg-blue-600 text-white"
          : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"}`
      }
    >
      {Icon && <Icon className="h-[13px] w-[13px] flex-shrink-0 opacity-80" />}
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

/* ── Top-level nav item (with icon, no indent) ──────────────────── */
function TopItem({ to, icon: Icon, label }: { to: string; icon?: any; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] font-semibold transition-colors duration-100
        ${isActive
          ? "bg-blue-600 text-white"
          : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"}`
      }
    >
      {Icon && <Icon className="h-[15px] w-[15px] flex-shrink-0" />}
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

/* ── Section label divider ──────────────────────────────────────── */
function Divider({ label }: { label: string }) {
  return (
    <div className="pt-3 pb-0.5 px-1">
      <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-gray-400">{label}</span>
    </div>
  );
}

/* ── Main sidebar ───────────────────────────────────────────────── */
export function AppSidebar() {
  const { isSuperAdmin } = useUserRole();
  const { user: currentUser } = useAuth();

  const [masterOpen,      setMasterOpen]      = useState(false);
  const [invoiceOpen,     setInvoiceOpen]     = useState(false);
  const [dayBizOpen,      setDayBizOpen]      = useState(false);
  const [stockOpen,       setStockOpen]       = useState(false);
  const [relationalOpen,  setRelationalOpen]  = useState(false);

  const { hasFeature, isLoading, isError, features, migrationsRun } = useFeatureAccess();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 2000);
    return () => clearTimeout(t);
  }, []);

  const allow = useCallback((key?: FeatureKey): boolean => {
    if (!key) return true;
    if (isLoading && !timedOut) return false;
    if (isError && (!features || features.length === 0)) return false;
    if (!isLoading && !isError && (!features || features.length === 0)) {
      return migrationsRun == null; // fail-open only when unknown
    }
    return hasFeature(key);
  }, [isLoading, timedOut, isError, features, migrationsRun, hasFeature]);

  const isDevUser = currentUser?.email === "dev@developer.local" || currentUser?.username === "dev";

  const masterItems = [
    { title: "Fuel Products",    url: "/master/fuel-products",         icon: Fuel,         key: "fuel_products"     },
    { title: "Lubricants",       url: "/master/lubricants",            icon: Droplet,      key: "lubricants"        },
    { title: "Credit Customer",  url: "/master/credit-customer",       icon: Users,        key: "credit_customers"  },
    { title: "Employees",        url: "/master/employees",             icon: UserCheck,    key: "employees"         },
    { title: "Expense Types",    url: "/master/expense-types",         icon: Tag,          key: "expense_types"     },
    { title: "Busi. Party",      url: "/master/busi-crd-debit-party",  icon: BookOpen,     key: "business_parties"  },
    { title: "Vendor",           url: "/master/vendor",                icon: Store,        key: "vendors"           },
    { title: "Swipe Machines",   url: "/master/swipe-machines",        icon: CreditCard,   key: "swipe_machines"    },
    { title: "Expiry Items",     url: "/master/expiry-items",          icon: AlertCircle,  key: "expiry_items"      },
    { title: "Tank & Nozzle",    url: "/master/tank-nozzel",           icon: Layers,       key: "tank_nozzle"       },
    { title: "Pump Setting",     url: "/master/pump-setting",          icon: Settings,     key: "pump_settings"     },
    { title: "DutyPay Shift",    url: "/master/dutypay-shift",         icon: Clock,        key: "duty_pay_shift"    },
    { title: "Print Templates",  url: "/master/print-templates",       icon: FileText,     key: "print_templates"   },
    { title: "Guest Entry",      url: "/master/guest-entry",           icon: Users,        key: "guest_entry"       },
    { title: "Denominations",    url: "/master/denominations",         icon: Wallet,       key: "denominations"     },
  ] as { title: string; url: string; icon: any; key: FeatureKey }[];

  const hasMaster     = masterItems.some(i => allow(i.key));
  const hasInvoice    = allow("liquid_purchase") || allow("lubs_purchase");
  const hasDayBiz     = ["day_assignings","daily_sale_rate","sale_entry","lub_sale","swipe",
                          "credit_sale","expenses","recovery","employee_cash_recovery",
                          "day_opening_stock","day_settlement"].some(k => allow(k as FeatureKey));
  const hasStock      = allow("stock_report") || allow("lub_loss") || allow("lubs_stock") || allow("minimum_stock");
  const hasRelational = ["interest_transactions","sheet_records","day_cash_report","tanker_sale",
                          "guest_sales","attendance","duty_pay","sales_officer","credit_requests",
                          "expiry_items","feedback"].some(k => allow(k as FeatureKey));

  /* Loading state */
  if (isLoading && !timedOut) {
    return (
      <Sidebar className="w-60 border-r border-gray-100 bg-white">
        <SidebarContent className="p-4">
          <Brand />
          <div className="flex items-center gap-2 mt-6 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Loading…</span>
          </div>
        </SidebarContent>
      </Sidebar>
    );
  }

  /* Developer sidebar */
  if (isDevUser) {
    return (
      <Sidebar className="w-60 border-r border-gray-100 bg-white">
        <SidebarContent className="p-3 flex flex-col h-full">
          <Brand />
          <div className="mt-4">
            <NavLink
              to="/developer-mode"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold transition-colors
                ${isActive ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`
              }
            >
              <ShieldCheck className="h-4 w-4" />
              🔧 Developer Mode
            </NavLink>
            <p className="text-[10px] text-gray-400 px-2 mt-2">Manage all pumps & users</p>
          </div>
        </SidebarContent>
      </Sidebar>
    );
  }

  /* Standard sidebar */
  return (
    <Sidebar className="w-60 border-r border-gray-100 bg-white">
      <SidebarContent className="px-2.5 py-3 overflow-y-auto">
        <Brand />

        <nav className="mt-3 space-y-0.5">

          {allow("dashboard") && <TopItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />}
          {isSuperAdmin       && <TopItem to="/organization-settings" icon={Building2} label="Organization" />}

          {hasMaster && (
            <>
              <Divider label="Master" />
              <Section label="Master" icon={MapPin} open={masterOpen} onToggle={() => setMasterOpen(v => !v)}>
                {masterItems.filter(i => allow(i.key)).map(i => (
                  <Item key={i.url} to={i.url} icon={i.icon} label={i.title} />
                ))}
              </Section>
            </>
          )}

          {hasInvoice && (
            <>
              <Divider label="Purchases" />
              <Section label="Invoice" icon={Receipt} open={invoiceOpen} onToggle={() => setInvoiceOpen(v => !v)}>
                {allow("liquid_purchase") && <Item to="/invoice/liquid-purchase" icon={Droplet}      label="Liquid Purchase" />}
                {allow("lubs_purchase")   && <Item to="/invoice/lubs-purchase"   icon={ShoppingBag}  label="Lubs Purchase"   />}
              </Section>
            </>
          )}

          {hasDayBiz && (
            <>
              <Divider label="Daily Ops" />
              <Section label="Day Business" icon={DollarSign} open={dayBizOpen} onToggle={() => setDayBizOpen(v => !v)}>
                {allow("day_assignings")        && <Item to="/day-assignings"                icon={ClipboardList} label="Day Assignings"     />}
                {allow("daily_sale_rate")       && <Item to="/daily-sale-rate"               icon={TrendingUp}    label="Daily Sale Rate"     />}
                {allow("sale_entry")            && <Item to="/sale-entry"                    icon={BarChart}      label="Sale Entry"           />}
                {allow("lub_sale")              && <Item to="/day-business/lubricants"       icon={Droplet}       label="Lubricants"           />}
                {allow("swipe")                 && <Item to="/day-business/swipe"            icon={CreditCard}    label="Swipe"                />}
                {allow("credit_sale")           && <Item to="/day-business/credit-sale"      icon={Banknote}      label="Credit Sale"          />}
                {allow("expenses")              && <Item to="/day-business/expenses"         icon={Wallet}        label="Expenses"             />}
                {allow("recovery")              && <Item to="/recovery"                      icon={UserCheck}     label="Recovery"             />}
                {allow("employee_cash_recovery")&& <Item to="/employee-cash-recovery"        icon={Users}         label="Emp Cash Recovery"    />}
                {allow("day_opening_stock")     && <Item to="/day-opening-stock"             icon={Package}       label="Day Opening Stock"    />}
                {allow("day_settlement")        && <Item to="/day-business/day-settlement"   icon={BarChart2}     label="Day Settlement"       />}
              </Section>
            </>
          )}

          <Divider label="Finance & Reports" />

          {allow("statement_generation")      && <TopItem to="/statements"          icon={FileText}    label="Statement Generation"  />}
          {allow("shift_sheet_entry")         && <TopItem to="/shift-sheet-entry"   icon={Calendar}    label="Shift Sheet Entry"      />}
          {allow("business_crdr_transactions")&& <TopItem to="/busi-crdr-trxn"      icon={Activity}    label="Busi. Cr/Dr Trxns"     />}
          {allow("vendor_transactions")       && <TopItem to="/vendor-transaction"  icon={Truck}       label="Vendor Transaction"     />}
          {allow("reports")                   && <TopItem to="/reports"             icon={BarChart2}   label="Reports"                />}
          {allow("generate_sale_invoice")     && <TopItem to="/sale-invoice"        icon={Receipt}     label="Generate Sale Invoice"  />}
          {allow("generated_invoices")        && <TopItem to="/generated-invoices"  icon={ClipboardList} label="Generated Invoices"  />}
          {allow("credit_limit_reports")      && <TopItem to="/credit-limit"        icon={TrendingUp}  label="Credit Limit Reports"   />}

          {hasStock && (
            <Section label="Product Stock" icon={Package} open={stockOpen} onToggle={() => setStockOpen(v => !v)}>
              {allow("stock_report")  && <Item to="/product-stock/stock-report"  icon={BarChart2}    label="Stock Report"    />}
              {allow("lub_loss")      && <Item to="/product-stock/lub-loss"       icon={AlertCircle}  label="Lub Loss"        />}
              {allow("lubs_stock")    && <Item to="/product-stock/lubs-stock"     icon={Layers}       label="Lubs Stock"      />}
              {allow("minimum_stock") && <Item to="/product-stock/minimum-stock"  icon={AlertCircle}  label="Minimum Stock"   />}
            </Section>
          )}

          {hasRelational && (
            <>
              <Divider label="Relational" />
              <Section label="Relational" icon={UsersRound} open={relationalOpen} onToggle={() => setRelationalOpen(v => !v)}>
                {allow("interest_transactions") && <Item to="/relational/interest-trans"   icon={DollarSign}    label="Interest Trans"    />}
                {allow("sheet_records")         && <Item to="/relational/sheet-records"    icon={BookOpen}      label="Sheet Records"     />}
                {allow("day_cash_report")       && <Item to="/relational/day-cash-report"  icon={Banknote}      label="Day Cash Report"   />}
                {allow("tanker_sale")           && <Item to="/tanker-sale"                 icon={Truck}         label="Tanker Sale"       />}
                {allow("guest_sales")           && <Item to="/guest-sales"                 icon={Users}         label="Guest Sales"       />}
                {allow("attendance")            && <Item to="/attendance-report"           icon={Calendar}      label="Attendance"        />}
                {allow("duty_pay")              && <Item to="/duty-pay"                    icon={Clock}         label="Duty Pay"          />}
                {allow("sales_officer")         && <Item to="/sales-officer"               icon={UserCheck}     label="Sales Officer"     />}
                {allow("credit_requests")       && <Item to="/credit-requests"             icon={CreditCard}    label="Credit Requests"   />}
                {allow("expiry_items")          && <Item to="/relational/expiry-items"     icon={AlertCircle}   label="Expiry Items"      />}
                {allow("feedback")              && <Item to="/feedback"                    icon={Star}          label="Feedback"          />}
              </Section>
            </>
          )}

        </nav>
      </SidebarContent>
    </Sidebar>
  );
}

/* ── Brand ──────────────────────────────────────────────────────── */
function Brand() {
  return (
    <div className="flex items-center gap-2 px-1 mb-1">
      <div className="h-6 w-6 rounded bg-blue-600 flex items-center justify-center flex-shrink-0">
        <Fuel className="h-3.5 w-3.5 text-white" />
      </div>
      <div>
        <p className="text-[13px] font-black text-blue-900 leading-none">PetroOne</p>
        <p className="text-[8px] font-semibold text-gray-400 uppercase tracking-widest mt-0.5">Fuel Management</p>
      </div>
    </div>
  );
}
